import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
// Components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import FAQDetails from "components/CBFAQ/components/FAQDetails";
// Styled Components
import { StyledFAQHeader, StyledFAQItem, StyledFAQSection, StyledFAQTitle, StyledFAQContent } from "./index.styles";
// Utils
import theme from "assets/theme";
import { searchFaqs } from "utils/data/faqData";
import { Stack } from "@mui/material";
import useMediaQueries from "utils/mediaQueries.utils";
import { useNavigate } from "react-router-dom";
import MKButton from "components/MKButton";
// Animations
const faqItemAnimation = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.6 },
};

const selectedFAQAnimation = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.3, ease: "easeInOut" },
};

const defaultStylesFaqLink = {
  letterSpacing: ".05rem",
  textDecoration: "underline",
  textUnderlineOffset: ".25rem",
  color: theme.palette.accent.main,
  "&:hover": {
    textShadow: `0px 0px 10px ${theme.palette.accent.main}`,
  },
};

const defaultStylesFaqButtons = {
  background: "transparent",
  border: "none",
  boxShadow: "none",
  padding: 0,
  "&:hover": { background: "transparent", border: "transparent", boxShadow: `none` },
  "&:focus": { background: "transparent", border: "transparent", boxShadow: `none` },
  "&:focus-visible": { background: "transparent", border: "transparent", boxShadow: `none` },
  "&:active": { background: "transparent", border: "transparent", boxShadow: `none` },
};

const FAQContentView = ({ initialCategory, textColor, component, variant }) => {
  const [currentFaqData, setCurrentFaqData] = useState([]);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { isLg } = useMediaQueries();

  useEffect(() => {
    const initialFaqs = searchFaqs("", initialCategory);
    setCurrentFaqData(initialFaqs);
  }, [initialCategory]);

  const handleClick = (event, faq) => {
    event.preventDefault();
    event.stopPropagation();
    if (expandedFAQ?.question === faq.question) {
      setExpandedFAQ(null);
    } else {
      setExpandedFAQ(faq);
    }
    return false;
  };

  const handleKeyDown = (event, faq) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      if (expandedFAQ?.question === faq.question) {
        setExpandedFAQ(null);
      } else {
        setExpandedFAQ(faq);
      }
    }
  };

  // const resetFaqs = () => {
  //   setExpandedFAQ(null);
  //   const initialFaqs = searchFaqs("", initialCategory);
  //   setCurrentFaqData(initialFaqs);
  // };

  const textColorStyle = textColor ? { color: `${textColor} !important` } : { color: theme.palette.primary.main };

  return (
    <MKBox id={`faq-section-${currentCategoryIndex}`}>
      <Stack sx={{ marginBottom: 3 }} direction={isLg ? "row" : "column"} spacing={isLg ? 4 : 2} alignItems={isLg ? "center" : "flex-start"}>
        <MKTypography
          ref={containerRef}
          variant={variant ? variant : "h3"}
          component={component ? component : "h3"}
          id="faq-section-title"
          sx={{ ...textColorStyle }}
        >
          Frequently Asked Questions
        </MKTypography>
        <Stack direction="row" spacing={2} sx={{ paddingBlock: ".2rem" }} alignItems="center">
          <MKButton size="small" sx={defaultStylesFaqButtons} onClick={() => navigate("/faq")}>
            <MKTypography variant="body2" component="p" sx={defaultStylesFaqLink}>
              View All FAQs
            </MKTypography>
          </MKButton>
          {/* <MKTypography
            variant="body2"
            component="p"
            sx={{
              color: theme.palette.white.main,
              fontWeight: "bold",
              borderLeft: `2px solid ${theme.palette.white.main}`,
              height: "20px",
            }}
          ></MKTypography> */}
          {/* <MKButton size="small" sx={defaultStylesFaqButtons} onClick={resetFaqs}>
            <MKTypography
              variant="body2"
              component="p"
              sx={{
                ...defaultStylesFaqLink,
              }}
            >
              Reset FAQs
            </MKTypography>
          </MKButton> */}
        </Stack>
      </Stack>
      <StyledFAQSection>
        {currentFaqData.map(({ question, answer, answerList, answerFooter, cta, keyTerms }, index) => {
          const isExpanded = expandedFAQ && expandedFAQ.question === question;
          const faqData = { question, answer, answerList, answerFooter, cta, keyTerms };
          return (
            <motion.div
              key={question}
              initial={faqItemAnimation.initial}
              animate={faqItemAnimation.animate}
              exit={faqItemAnimation.exit}
              transition={faqItemAnimation.transition}
            >
              <StyledFAQItem
                onClick={(event) => handleClick(event, faqData)}
                onKeyDown={(event) => handleKeyDown(event, faqData)}
                isSelected={isExpanded}
                textColor={textColor}
                component="div"
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  outline: "none",
                }}
              >
                <StyledFAQHeader>
                  <StyledFAQTitle id={`faq-question-${index}`} variant={"h6"} component={"h3"} className="hover-text" textColorStyle={textColorStyle}>
                    {question}
                  </StyledFAQTitle>
                  <KeyboardArrowRightIcon
                    sx={{
                      color: textColor === "#fff" ? theme.palette.accent.main : theme.palette.secondary.main,
                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.3s ease-in-out",
                    }}
                  />
                </StyledFAQHeader>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      key={`faq-content-${question}`}
                      initial={selectedFAQAnimation.initial}
                      animate={selectedFAQAnimation.animate}
                      exit={selectedFAQAnimation.exit}
                      transition={selectedFAQAnimation.transition}
                      style={{ overflow: "hidden" }}
                    >
                      <StyledFAQContent>
                        <FAQDetails item={expandedFAQ} textColor={textColor} />
                      </StyledFAQContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </StyledFAQItem>
            </motion.div>
          );
        })}
      </StyledFAQSection>
    </MKBox>
  );
};

export default FAQContentView;
